"use client";

import React from "react";
import type { Option } from "@/app/constants/creatorNetwork";

type ChipGroupProps = {
  label: string;
  hint?: string;
  options: Option[];
  mode: "single" | "multi";
  value: string | string[];
  onChange: (value: string | string[]) => void;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  error?: string | null;
};

export default function ChipGroup({
  label,
  hint,
  options,
  mode,
  value,
  onChange,
  otherValue,
  onOtherChange,
  error,
}: ChipGroupProps) {
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  const isSelected = (optValue: string) => selected.includes(optValue);

  const toggle = (optValue: string) => {
    if (mode === "single") {
      onChange(optValue === value ? "" : optValue);
      return;
    }
    if (isSelected(optValue)) {
      onChange(selected.filter((v) => v !== optValue));
    } else {
      onChange([...selected, optValue]);
    }
  };

  const showOtherInput =
    onOtherChange !== undefined &&
    options.some((o) => o.value === "others") &&
    isSelected("others");

  return (
    <div className="relative w-full sm:px-4">
      <span className="block text-white mb-3">
        {label}
        {hint && (
          <span className="block sm:inline text-white/50 text-sm mt-1 sm:mt-0 sm:ml-2">
            {hint}
          </span>
        )}
      </span>
      <div className="flex flex-wrap gap-2.5" role="group" aria-label={label}>
        {options.map((opt) => {
          const active = isSelected(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(opt.value)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-150 ${
                active
                  ? "bg-green border-green text-darkGrey"
                  : "border-white/30 text-white/80 hover:border-white/60"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {showOtherInput && (
        <input
          type="text"
          value={otherValue || ""}
          onChange={(e) => onOtherChange?.(e.target.value)}
          placeholder="Please specify..."
          className="!w-full md:!max-w-md mt-4"
        />
      )}

      {error && <div className="mt-2 text-red text-sm">{error}</div>}
    </div>
  );
}
